// Globals
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '@/models/router.model';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getAdminDetails, updateAdminDetails, createAdmin, getAdminsList } from '../../feature/actionCreators';
import { selectQuery, usersListSelector } from '../../feature/selectors';

// Components
import { IFormInstance, MessageModal, showSuccessToast } from '@/components';
import { Skeleton } from '@/ui';
import { CreateAdminForm } from '../../components';
import { StyledPageWrapper } from './CreateAdminContainerStyles';

// Models
import { IAdminProfileResponse } from '@/models/admins.model';

// Helpers
import { TAdminFormValues, TGetAdminsQuery } from '../../helpers/types';
import { AdminDataNormalizer, AdminDetailsDTO, GetAdminsListDTO } from '../../dtos';
import { useModal } from '@/hooks';

type TCreateAdminContainer = {
  id?: string;
  form: IFormInstance;
};

const CreateAdminContainer = ({ id, form }: TCreateAdminContainer) => {
  const dispatch = useAppDispatch();
  const { isLoading, adminDetails } = useAppState(usersListSelector);
  const query = useAppState(selectQuery);
  const { t } = useTranslation(['profile', 'usersList']);
  const navigate = useNavigate();
  const { type } = useParams();
  const isEditMode = Boolean(id && id !== 'create');
  const updateAction = isEditMode ? updateAdminDetails : createAdmin;
  const {
    isOpened: isActivationModalOpen,
    openModal: openActivationModal,
    closeModal: closeActivationModal,
  } = useModal();

  const navigateToList = useCallback(() => {
    navigate(`${ROUTES.USERS_LIST_INDEX}/${type}`);
  }, [navigate, type]);

  const handleActivationModalClose = useCallback(() => {
    closeActivationModal();
    navigateToList();
  }, [closeActivationModal, navigateToList]);

  const handleSubmit = useCallback(
    async (values: TAdminFormValues) => {
      const adminProfileDto = new AdminDetailsDTO(values);
      await dispatch(updateAction({ ...adminDetails, ...adminProfileDto, id: isEditMode ? id : undefined })).unwrap();

      const dto = new GetAdminsListDTO(query as TGetAdminsQuery);
      await dispatch(getAdminsList(dto));

      if (isEditMode) {
        showSuccessToast({ message: t('profile_data_saved') });
        navigateToList();
        return;
      }

      openActivationModal();
    },
    [adminDetails, dispatch, id, isEditMode, navigateToList, openActivationModal, query, t, updateAction],
  );

  const adminData = adminDetails ? new AdminDataNormalizer(adminDetails as IAdminProfileResponse) : undefined;

  useEffect(() => {
    if (isEditMode) dispatch(getAdminDetails(id as string));
  }, [id, isEditMode]);

  useEffect(() => {
    // initialValues only apply on the first Form mount, so push freshly fetched
    // details into the shared form instance explicitly on every reopen.
    if (isEditMode && adminDetails) {
      form.setFieldsValue(new AdminDataNormalizer(adminDetails as IAdminProfileResponse));
    }
  }, [adminDetails, isEditMode]);

  return (
    <StyledPageWrapper>
      {isLoading ? (
        <Skeleton />
      ) : (
        <CreateAdminForm
          handleSubmit={handleSubmit}
          isLoading={isLoading}
          form={form}
          initialValues={isEditMode ? adminData : null}
          isEditMode={isEditMode}
        />
      )}
      <MessageModal
        isOpened={isActivationModalOpen}
        message={t('usersList:account_created_title')}
        additionalMessage={t('usersList:account_created_activation_text')}
        handleClose={handleActivationModalClose}
        maskClosable={false}
      />
    </StyledPageWrapper>
  );
};

export default CreateAdminContainer;
